import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyGlassesCatalog – IntersectionObserver-based lazy catalog.
 *
 * Every item gets a lightweight sentinel wrapper.  Only when the wrapper
 * enters the viewport (+ rootMargin) does the real card render.
 * Items that have already been revealed stay mounted (no unmounting on scroll-out).
 *
 * `initialCount` items are rendered immediately (no observer needed) so the
 * first screenful paints instantly.
 */
export default function LazyGlassesCatalog({
  items = [],
  selectedId,
  onSelect,
  renderItem,
  initialCount = 6,
  rootMargin = '200px',
  containerStyle = {},
  gridStyle = {},
}) {
  return (
    <div style={containerStyle}>
      <div style={gridStyle}>
        {items.map((item, idx) => (
          <LazySlot
            key={item.id}
            item={item}
            idx={idx}
            eager={idx < initialCount}
            selectedId={selectedId}
            onSelect={onSelect}
            renderItem={renderItem}
            rootMargin={rootMargin}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * LazySlot – wraps a single catalog item.
 * If `eager` is true the real content renders immediately.
 * Otherwise a skeleton placeholder is shown until the element
 * enters the extended viewport (rootMargin).
 */
function LazySlot({ item, idx, eager, selectedId, onSelect, renderItem, rootMargin }) {
  const [revealed, setRevealed] = useState(eager);
  const ref = useRef(null);

  useEffect(() => {
    if (eager || revealed) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [eager, revealed, rootMargin]);

  if (revealed) {
    return renderItem(item, selectedId, onSelect, idx);
  }

  // Placeholder — same aspect ratio as a real card so layout doesn't shift
  return (
    <div
      ref={ref}
      style={{
        borderRadius: 14,
        background: 'rgba(0,0,0,0.06)',
        minHeight: 100,
        animation: 'skeleton-load 1.5s ease-in-out infinite',
      }}
    />
  );
}

/**
 * LazyReveal – generic IntersectionObserver wrapper.
 * Renders children only once the element enters the viewport.
 * If `eager` is true, children render immediately.
 */
export function LazyReveal({ children, eager = false, rootMargin = '200px', placeholder = null }) {
  const [revealed, setRevealed] = useState(eager);
  const ref = useRef(null);

  // Respond to eager becoming true after mount (e.g. progressive batch reveal)
  useEffect(() => {
    if (eager) setRevealed(true);
  }, [eager]);

  useEffect(() => {
    if (eager || revealed) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [eager, revealed, rootMargin]);

  if (revealed) return children;

  return placeholder || (
    <div
      ref={ref}
      style={{
        borderRadius: 12,
        background: 'rgba(255,255,255,0.04)',
        minWidth: 64,
        minHeight: 48,
      }}
    />
  );
}

/**
 * SkeletonCard - Loading placeholder for glasses card
 */
export function SkeletonCard({ style = {} }) {
  return (
    <div
      style={{
        borderRadius: 12,
        background: 'rgba(0, 0, 0, 0.1)',
        padding: 8,
        aspectRatio: '1',
        animation: 'skeleton-load 1.5s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

/**
 * LazyImageWithFallback - Image that shows skeleton while loading
 */
export function LazyImageWithFallback({
  src,
  alt = '',
  containerStyle = {},
  imgStyle = {},
  onLoad,
  priority = false,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <div style={containerStyle}>
      {!isLoaded && !hasError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.08)',
            borderRadius: 'inherit',
            animation: 'skeleton-load 1.5s ease-in-out infinite',
          }}
        />
      )}
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        {...(priority ? { fetchpriority: 'high' } : {})}
        onLoad={() => {
          setIsLoaded(true);
          onLoad?.();
        }}
        onError={() => {
          setHasError(true);
          setIsLoaded(true);
        }}
        style={{
          ...imgStyle,
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 200ms ease',
        }}
      />
      <style>{`
        @keyframes skeleton-load {
          0%, 100% { background-color: rgba(0, 0, 0, 0.08); }
          50% { background-color: rgba(0, 0, 0, 0.12); }
        }
      `}</style>
    </div>
  );
}
